import { dedent } from 'ts-dedent'
import { v4 as uuid4 } from 'uuid'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

const predicates = new Map([
  ['intersects', 'ST_INTERSECTS'],
  ['contains', 'ST_CONTAINS'],
  ['covers', 'ST_COVERS']
])

class SpatialJoinSF extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.spatialjoin',
        group: 'Joins',
        title: 'Spatial Join',
        description: `
    This component performs a spatial join between two tables, based on a spatial predicate.
    `,
        starred: true
      },
      [
        {
          name: 'maintable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Main table'
        },
        {
          name: 'secondarytable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Secondary table'
        },
        {
          name: 'maincolumn',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in main table',
          parent: 'maintable',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'secondarycolumn',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in secondary table',
          parent: 'secondarytable',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'predicate',
          type: WorkflowComponentParamType.Selection,
          title: 'Spatial predicate',
          default: predicates.keys().next().value,
          optional: true,
          options: Array.from(predicates.keys())
        }
      ]
    )
    this.supportedProviders = [Provider.Snowflake]
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const preparedParams = this._prepareParameters(params)
    const select = `SELECT
            _main.*, %s
            FROM
              ${connection.quoteName(
                preparedParams.maintable as string
              )} AS _main,
              ${connection.quoteName(
                preparedParams.secondarytable as string
              )} AS _joined
            WHERE ${predicates.get(params.predicate as string)}(
              _main.${params.maincolumn},
              _joined.${params.secondarycolumn}
            )`
    const createTable = connection.getCreateTempTableQuery(
      outputTablename,
      select,
      codeType,
      null
    )
    let query = ''
    const storedProc = codeType === WorkflowCodeType.StoredProcedure
    switch (connection.type) {
      case Provider.Snowflake: {
        let datasetname, schemaname, tablename, storedProcTable
        if (storedProc) {
          ;[datasetname, schemaname] = connection.tempStoragePath.split('.')
          tablename = `table_${uuid4().replace(/-/g, '_')}`
          storedProcTable = connection.quoteName(
            `${connection.tempStoragePath}.${tablename}`
          )
        } else {
          ;[datasetname, schemaname, tablename] = (
            preparedParams.secondarytable as string
          ).split('.')
        }
        query = dedent`DECLARE alias STRING;
                BEGIN
                  ${
                    storedProc
                      ? dedent`
                          CREATE TABLE ${storedProcTable} AS
                          SELECT * FROM ${connection.quoteName(
                            preparedParams.secondarytable as string
                          )}
                          WHERE 1=0;`
                      : ''
                  }
                  LET res resultset := (EXECUTE IMMEDIATE '
                    with __alias AS(
                    SELECT CONCAT(''_joined.'', column_name, '' AS '', column_name, ''_joined'') col_alias
                    FROM ${datasetname}.INFORMATION_SCHEMA.COLUMNS
                    WHERE table_name = ''${tablename.toUpperCase()}'' and
                    table_schema = ''${schemaname.toUpperCase()}'' and
                    table_catalog = ''${datasetname.toUpperCase()}''
                    )
                    SELECT LISTAGG(col_alias, '', '') aliases
                    FROM __alias
                  ');
                  LET cur_res CURSOR FOR res;
                  OPEN cur_res;
                  FETCH cur_res INTO alias;
                  ${storedProc ? `DROP TABLE ${storedProcTable};` : ''}
                  EXECUTE IMMEDIATE
                    REPLACE(
                      'BEGIN
                        ${createTable}
                      END;',
                      '%s',
                      alias
                    );
                END;`
        break
      }
      default:
        return null
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns }: GetOutputSchemaParams): TableSchema {
    const schema = columns.maintable.map((column) => {
      return {
        name: `${column.name}`,
        type: column.type
      }
    })
    columns.secondarytable.forEach((column) => {
      schema.push({
        name: `${column.name}_joined`,
        type: column.type
      })
    })
    return schema
  }
}

export { SpatialJoinSF }
